/**
 * Interactions service
 * Handles CRUD operations for interactions at organization and location level
 * Supports participants tagging, file attachments up to 5GB, and full search/filter
 */

import { supabase } from "./supabase";
import type {
  Interaction,
  InteractionFilters,
  InteractionStats,
  InteractionTemplate,
  InteractionParticipant,
  InteractionAttachment,
} from "../types/interaction.types";
import {
  InteractionType,
  InteractionDirection,
  InteractionOutcome,
  InteractionSentiment,
} from "../types/interaction.types";
import type {
  UUID,
  PaginatedResponse,
  PaginationParams,
} from "../types/common.types";
import { sanitizePostgrestSearchTerm } from "../utils/postgrestSearch";

/**
 * Get all interactions with pagination and filters
 */
export const getInteractions = async (
  params: PaginationParams,
  filters?: InteractionFilters,
): Promise<PaginatedResponse<Interaction>> => {
  const {
    page = 1,
    limit = 10,
    sortBy = "interaction_date",
    sortOrder = "desc",
  } = params;
  const start = (page - 1) * limit;
  const end = start + limit - 1;

  let query = supabase.from("interactions").select("*", { count: "exact" });

  // Apply filters
  if (filters) {
    if (filters.organization_id) {
      query = query.eq("organization_id", filters.organization_id);
    }
    if (filters.location_id) {
      query = query.eq("location_id", filters.location_id);
    }
    if (filters.contact_id) {
      query = query.eq("contact_id", filters.contact_id);
    }
    if (filters.user_id) {
      query = query.eq("user_id", filters.user_id);
    }
    // New filters for purpose and method
    if (filters.purpose && filters.purpose.length > 0) {
      query = query.in("purpose", filters.purpose);
    }
    if (filters.method && filters.method.length > 0) {
      query = query.in("method", filters.method);
    }
    // Legacy filters
    if (filters.type && filters.type.length > 0) {
      query = query.in("type", filters.type);
    }
    if (filters.direction) {
      query = query.eq("direction", filters.direction);
    }
    if (filters.outcome && filters.outcome.length > 0) {
      query = query.in("outcome", filters.outcome);
    }
    if (filters.date_from) {
      query = query.gte("interaction_date", filters.date_from);
    }
    if (filters.date_to) {
      query = query.lte("interaction_date", filters.date_to);
    }
    if (filters.tags && filters.tags.length > 0) {
      query = query.contains("tags", filters.tags);
    }
    if (filters.category) {
      query = query.eq("category", filters.category);
    }
    if (filters.sentiment) {
      query = query.eq("sentiment", filters.sentiment);
    }
    if (filters.subject_id) {
      query = query.eq("subject_id", filters.subject_id);
    }
    if (filters.activity_type_id) {
      query = query.eq("activity_type_id", filters.activity_type_id);
    }
    if (filters.search) {
      // The term is interpolated into PostgREST's .or() grammar: strip the delimiters so it can
      // neither break the filter nor add predicates. The .or() is ANDed with every filter above,
      // so an organization-scoped search never reaches another organization's rows.
      const term = sanitizePostgrestSearchTerm(filters.search);
      if (term) {
        query = query.or(
          `title.ilike.*${term}*,description.ilike.*${term}*,call_plan.ilike.*${term}*,results.ilike.*${term}*`,
        );
      }
    }
  }

  // Get paginated data with count. Newest first by activity date, then time, then creation, so
  // rows on the same date keep a stable order across pages.
  let ordered = query.order(sortBy, { ascending: sortOrder === "asc" });
  if (sortBy === "interaction_date") {
    ordered = ordered
      .order("interaction_time", {
        ascending: sortOrder === "asc",
        nullsFirst: false,
      })
      .order("created_at", { ascending: sortOrder === "asc" });
  }
  const { data, error, count } = await ordered.range(start, end);

  if (error) {
    throw error;
  }

  return {
    data: data as Interaction[],
    total: count || 0,
    page,
    limit,
    totalPages: Math.ceil((count || 0) / limit),
  };
};

/**
 * Get interaction by ID
 */
export const getInteraction = async (id: UUID): Promise<Interaction> => {
  const { data, error } = await supabase
    .from("interactions")
    // Plain row: interactions has two foreign keys to profiles (user_id, created_by), so an unqualified
    // profiles embed is ambiguous and PostgREST refuses it (PGRST201). The only caller is the
    // Interactions page deep link (?interaction=<id>), which needs the row itself.
    .select("*")
    .eq("id", id)
    .single();

  if (error) {
    throw error;
  }
  if (!data) {
    throw new Error("Interaction not found");
  }

  return data as Interaction;
};

/**
 * Create new interaction
 */
export const createInteraction = async (
  interaction: Omit<Interaction, "id" | "created_at" | "updated_at">,
): Promise<Interaction> => {
  const { data, error } = await supabase
    .from("interactions")
    .insert(interaction)
    .select()
    .single();

  if (error) {
    throw error;
  }

  // There used to be a follow-up `contacts.update({ last_contacted_at })` here. That column does not
  // exist on QA or PROD, so every call fired a PATCH that PostgREST refused (PGRST204) and the
  // unchecked error was discarded. Removed rather than "fixed": nothing reads a last-contacted
  // value, and prospects.last_contact_date is maintained server-side by the
  // update_prospect_interactions trigger on interactions INSERT.
  return data as Interaction;
};

/**
 * Update interaction
 */
export const updateInteraction = async (
  id: UUID,
  updates: Partial<Interaction>,
): Promise<Interaction> => {
  const { data, error } = await supabase
    .from("interactions")
    .update(updates)
    .eq("id", id)
    .select();

  if (error) {
    throw error;
  }
  // RLS filters a refused UPDATE to zero rows without an error (e.g. a reward or internal record
  // for a user who may not change it). Report it instead of looking saved.
  if (!data || data.length === 0) {
    throw new Error(
      "This activity was not updated: you do not have permission to change it.",
    );
  }
  return data[0] as Interaction;
};

/**
 * Delete interaction. Throws when nothing was deleted (RLS filters a refused DELETE to zero rows).
 */
export const deleteInteraction = async (id: UUID): Promise<void> => {
  const { data, error } = await supabase
    .from("interactions")
    .delete()
    .eq("id", id)
    .select("id");

  if (error) {
    throw error;
  }
  if (!data || data.length === 0) {
    throw new Error(
      "This activity was not deleted: you do not have permission to delete it.",
    );
  }
};

/**
 * Get interactions by contact
 */
export const getContactInteractions = async (
  contactId: UUID,
  limit: number = 10,
): Promise<Interaction[]> => {
  const { data, error } = await supabase
    .from("interactions")
    .select("*, user:profiles!interactions_user_id_fkey(*)")
    .eq("contact_id", contactId)
    .order("interaction_date", { ascending: false })
    .limit(limit);

  if (error) {
    throw error;
  }
  return data as Interaction[];
};

/**
 * Get interaction statistics
 */
export const getInteractionStats = async (
  organizationId: UUID,
  dateFrom?: string,
  dateTo?: string,
): Promise<InteractionStats> => {
  const now = new Date();
  const startOfWeek = new Date(now.setDate(now.getDate() - now.getDay()));
  const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);

  let query = supabase
    .from("interactions")
    .select("*")
    .eq("organization_id", organizationId);

  if (dateFrom) {
    query = query.gte("interaction_date", dateFrom);
  }
  if (dateTo) {
    query = query.lte("interaction_date", dateTo);
  }

  const { data, error } = await query;

  if (error) {
    throw error;
  }

  const interactions = data as Interaction[];

  // Calculate statistics
  const stats: InteractionStats = {
    total: interactions.length,
    by_type: {} as Record<InteractionType, number>,
    by_direction: {} as Record<InteractionDirection, number>,
    by_outcome: {} as Record<InteractionOutcome, number>,
    by_sentiment: {} as Record<InteractionSentiment, number>,
    this_week: 0,
    this_month: 0,
    average_per_contact: 0,
  };

  // Initialize counters
  Object.values(InteractionType).forEach((type) => {
    stats.by_type[type] = 0;
  });
  Object.values(InteractionDirection).forEach((direction) => {
    stats.by_direction[direction] = 0;
  });
  Object.values(InteractionOutcome).forEach((outcome) => {
    stats.by_outcome[outcome] = 0;
  });
  Object.values(InteractionSentiment).forEach((sentiment) => {
    stats.by_sentiment[sentiment] = 0;
  });

  // Count interactions
  interactions.forEach((interaction) => {
    stats.by_type[interaction.type]++;
    stats.by_direction[interaction.direction]++;
    if (interaction.outcome) {
      stats.by_outcome[interaction.outcome]++;
    }
    if (interaction.sentiment) {
      stats.by_sentiment[interaction.sentiment]++;
    }

    const interactionDate = new Date(interaction.interaction_date);
    if (interactionDate >= startOfWeek) {
      stats.this_week++;
    }
    if (interactionDate >= startOfMonth) {
      stats.this_month++;
    }
  });

  // Calculate average per contact
  const { count: contactCount } = await supabase
    .from("contacts")
    .select("*", { count: "exact", head: true })
    .eq("organization_id", organizationId);

  stats.average_per_contact = contactCount
    ? interactions.length / contactCount
    : 0;

  return stats;
};

/**
 * Get interaction templates
 */
export const getInteractionTemplates = async (
  organizationId: UUID,
): Promise<InteractionTemplate[]> => {
  const { data, error } = await supabase
    .from("interaction_templates")
    .select("*")
    .eq("organization_id", organizationId)
    .order("name");

  if (error) {
    throw error;
  }
  return data as InteractionTemplate[];
};

/**
 * Create interaction template
 */
export const createInteractionTemplate = async (
  template: Omit<InteractionTemplate, "id" | "created_at" | "updated_at">,
): Promise<InteractionTemplate> => {
  const { data, error } = await supabase
    .from("interaction_templates")
    .insert(template)
    .select()
    .single();

  if (error) {
    throw error;
  }
  return data as InteractionTemplate;
};

/**
 * Create interaction from template
 */
export const createInteractionFromTemplate = async (
  templateId: UUID,
  contactId: UUID,
  userId: UUID,
  organizationId: UUID,
  overrides?: Partial<Interaction>,
): Promise<Interaction> => {
  const { data: template, error: templateError } = await supabase
    .from("interaction_templates")
    .select("*")
    .eq("id", templateId)
    .single();

  if (templateError) {
    throw templateError;
  }
  if (!template) {
    throw new Error("Template not found");
  }

  return createInteraction({
    organization_id: organizationId,
    contact_id: contactId,
    user_id: userId,
    type: template.type,
    direction: "outbound",
    subject: template.subject_template,
    description: template.description_template,
    interaction_date: new Date().toISOString(),
    tags: template.tags,
    category: template.category,
    ...overrides,
  } as Omit<Interaction, "id" | "created_at" | "updated_at">);
};

/**
 * Log phone call
 */
export const logPhoneCall = async (
  contactId: UUID,
  userId: UUID,
  organizationId: UUID,
  direction: InteractionDirection,
  outcome: InteractionOutcome,
  notes: string,
  durationMinutes?: number,
): Promise<Interaction> => {
  return createInteraction({
    organization_id: organizationId,
    contact_id: contactId,
    user_id: userId,
    type: "phone_call",
    direction,
    outcome,
    description: notes,
    interaction_date: new Date().toISOString(),
    duration_minutes: durationMinutes,
  } as Omit<Interaction, "id" | "created_at" | "updated_at">);
};

/**
 * Log email
 */
export const logEmail = async (
  contactId: UUID,
  userId: UUID,
  organizationId: UUID,
  direction: InteractionDirection,
  subject: string,
  body: string,
): Promise<Interaction> => {
  return createInteraction({
    organization_id: organizationId,
    contact_id: contactId,
    user_id: userId,
    type: "email",
    direction,
    subject,
    description: body,
    interaction_date: new Date().toISOString(),
    email_subject: subject,
  } as Omit<Interaction, "id" | "created_at" | "updated_at">);
};

/**
 * Get interaction participants
 */
export const getInteractionParticipants = async (
  interactionId: UUID,
): Promise<InteractionParticipant[]> => {
  const { data, error } = await supabase
    .from("interaction_participants")
    .select("*")
    .eq("interaction_id", interactionId)
    .order("created_at");

  if (error) {
    throw error;
  }
  return data.map((p) => ({
    id: p.participant_id,
    type: p.participant_type,
    name: p.participant_name,
  }));
};

/**
 * Add participant to interaction
 */
export const addInteractionParticipant = async (
  interactionId: UUID,
  participant: InteractionParticipant,
): Promise<void> => {
  const { error } = await supabase.from("interaction_participants").insert({
    interaction_id: interactionId,
    participant_type: participant.type,
    participant_id: participant.id,
    participant_name: participant.name,
  });

  if (error) {
    throw error;
  }
};

/**
 * Remove participant from interaction
 */
export const removeInteractionParticipant = async (
  interactionId: UUID,
  participantType: "contact" | "user",
  participantId: UUID,
): Promise<void> => {
  const { error } = await supabase
    .from("interaction_participants")
    .delete()
    .eq("interaction_id", interactionId)
    .eq("participant_type", participantType)
    .eq("participant_id", participantId);

  if (error) {
    throw error;
  }
};

/**
 * Get interaction attachments
 */
export const getInteractionAttachments = async (
  interactionId: UUID,
): Promise<InteractionAttachment[]> => {
  const { data, error } = await supabase
    .from("interaction_attachments")
    .select("*")
    .eq("interaction_id", interactionId)
    .order("created_at");

  if (error) {
    throw error;
  }
  return data as InteractionAttachment[];
};

/**
 * Add attachment to interaction
 */
export const addInteractionAttachment = async (
  interactionId: UUID,
  fileName: string,
  fileUrl: string,
  fileSize: number,
  fileType: string,
  uploadedBy: UUID,
): Promise<InteractionAttachment> => {
  const { data, error } = await supabase
    .from("interaction_attachments")
    .insert({
      interaction_id: interactionId,
      file_name: fileName,
      file_url: fileUrl,
      file_size: fileSize,
      file_type: fileType,
      uploaded_by: uploadedBy,
    })
    .select()
    .single();

  if (error) {
    throw error;
  }
  return data as InteractionAttachment;
};

/**
 * Remove attachment from interaction
 */
export const removeInteractionAttachment = async (
  attachmentId: UUID,
): Promise<void> => {
  const { error } = await supabase
    .from("interaction_attachments")
    .delete()
    .eq("id", attachmentId);

  if (error) {
    throw error;
  }
};

// =====================================================
// SUPPORT TASK ACTIVITY
// =====================================================

/**
 * Payload for SupportTasks "Log interaction with customer". Filed under the CUSTOMER: their contact and
 * their organization, or none (interactions_org_or_reward_check allows an organization-less row when it
 * carries support_task_id, migration 2026-10-06-321). Never the operator's organization. support_task_id
 * is sent on insert; linkInteractionToTask still writes support_tasks.interaction_id.
 */
export const buildSupportTaskInteraction = (params: {
  taskId: UUID;
  contactId: UUID;
  customerOrganizationId: UUID | null;
  userId: UUID;
  title: string;
  method: string;
  description: string;
  outcome?: InteractionOutcome;
  nextFollowUpDate?: string;
  tags?: string[];
  now?: Date;
}): Record<string, unknown> => ({
  contact_id: params.contactId,
  user_id: params.userId,
  organization_id: params.customerOrganizationId,
  support_task_id: params.taskId,
  title: params.title,
  purpose: "follow-up",
  method: params.method,
  interaction_date: localDateAndTime(params.now).interaction_date,
  description: params.description,
  outcome: params.outcome,
  next_follow_up_date: params.nextFollowUpDate,
  tags: params.tags,
});

// =====================================================
// REWARD INTERACTION LOGGING
// =====================================================

/**
 * The five interaction types an event reward writes. They must stay in step with
 * interactions_type_check and interactions_org_or_reward_check (migration 2026-10-05-220).
 */
export const REWARD_INTERACTION_TYPES = [
  InteractionType.VOUCHER_ISSUED,
  InteractionType.OFFER_LINK_SENT,
  InteractionType.OFFER_LINK_COPIED,
  InteractionType.PHYSICAL_DISTRIBUTED,
  InteractionType.PHYSICAL_SHIPPED,
] as const;

/**
 * Exactly what a reward log sends to public.interactions. organization_id, user_id attribution and
 * created_by are NOT trusted from here: the trg_interactions_reward_org trigger derives the
 * organization from the event's event_organizations link (NULL when the event has none) and stamps
 * user_id/created_by with auth.uid(). user_id is still sent so the row is complete for service-role
 * writers and older databases.
 */
export interface RewardInteractionInsert {
  contact_id: UUID;
  user_id: UUID;
  event_id?: UUID;
  cta_id?: UUID;
  voucher_id?: UUID;
  type: (typeof REWARD_INTERACTION_TYPES)[number];
  title: string;
  purpose: "other";
  // interactions_method_check allows only phone_call, meeting, email, in_person, event.
  method: "in_person" | "email" | "event";
  description: string;
  interaction_date: string; // YYYY-MM-DD in the operator's local time (column is DATE)
  interaction_time: string; // HH:MM:SS local
  reward_type: "voucher" | "url_offer" | "physical_good";
  direction: InteractionDirection.OUTBOUND;
  url_shared?: string;
  metadata?: Record<string, unknown>;
}

const pad2 = (n: number) => String(n).padStart(2, "0");

/** Local calendar date and time. interaction_date is a DATE column; toISOString() would store the
 * UTC date, which is tomorrow for an evening event in the US. */
export const localDateAndTime = (
  now: Date = new Date(),
): { interaction_date: string; interaction_time: string } => ({
  interaction_date: `${now.getFullYear()}-${pad2(now.getMonth() + 1)}-${pad2(now.getDate())}`,
  interaction_time: `${pad2(now.getHours())}:${pad2(now.getMinutes())}:${pad2(now.getSeconds())}`,
});

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Insert a reward interaction. Throws the PostgREST error; callers decide how to surface it. */
export const insertRewardInteraction = async (
  row: RewardInteractionInsert,
): Promise<Interaction> => {
  const { data, error } = await supabase
    .from("interactions")
    .insert(row)
    .select()
    .single();
  if (error) {
    throw error;
  }
  return data as Interaction;
};

export const buildVoucherIssuedInteraction = (
  params: {
    contactId: UUID;
    userId: UUID;
    eventId?: UUID;
    ctaId?: UUID;
    voucherId?: UUID | null;
    voucherCode: string;
    voucherAmount: number;
    notes?: string;
  },
  now: Date = new Date(),
): RewardInteractionInsert => ({
  contact_id: params.contactId,
  user_id: params.userId,
  event_id: params.eventId,
  cta_id: params.ctaId,
  // generate-offer returns the voucher id as voucherId / fulfillmentId depending on the path; only
  // a real uuid is sent so a missing id never fails the insert on the voucher_id foreign key.
  voucher_id:
    params.voucherId && UUID_RE.test(params.voucherId)
      ? params.voucherId
      : undefined,
  type: InteractionType.VOUCHER_ISSUED,
  title: `Voucher Issued: ${params.voucherCode}`,
  purpose: "other",
  method: "in_person",
  description:
    params.notes ||
    `Voucher code ${params.voucherCode} ($${params.voucherAmount.toFixed(2)}) issued`,
  ...localDateAndTime(now),
  reward_type: "voucher",
  direction: InteractionDirection.OUTBOUND,
  metadata: {
    voucher_code: params.voucherCode,
    voucher_amount: params.voucherAmount,
  },
});

export const buildOfferLinkSharedInteraction = (
  params: {
    contactId: UUID;
    userId: UUID;
    eventId?: UUID;
    ctaId?: UUID;
    url: string;
    ctaName: string;
    sentViaEmail: boolean;
    notes?: string;
  },
  now: Date = new Date(),
): RewardInteractionInsert => ({
  contact_id: params.contactId,
  user_id: params.userId,
  event_id: params.eventId,
  cta_id: params.ctaId,
  type: params.sentViaEmail
    ? InteractionType.OFFER_LINK_SENT
    : InteractionType.OFFER_LINK_COPIED,
  title: `Offer Link ${params.sentViaEmail ? "Sent" : "Copied"}: ${params.ctaName}`,
  purpose: "other",
  method: params.sentViaEmail ? "email" : "in_person",
  description:
    params.notes ||
    `Offer link for "${params.ctaName}" ${params.sentViaEmail ? "sent via email" : "copied for manual sharing"}`,
  ...localDateAndTime(now),
  reward_type: "url_offer",
  url_shared: params.url,
  direction: InteractionDirection.OUTBOUND,
});

export const buildPhysicalDistributedInteraction = (
  params: {
    contactId: UUID;
    userId: UUID;
    eventId?: UUID;
    ctaId?: UUID;
    itemName: string;
    distributionMethod: "handed_out" | "shipped";
    trackingNumber?: string;
    notes?: string;
  },
  now: Date = new Date(),
): RewardInteractionInsert => {
  const shipped = params.distributionMethod === "shipped";
  return {
    contact_id: params.contactId,
    user_id: params.userId,
    event_id: params.eventId,
    cta_id: params.ctaId,
    type: shipped
      ? InteractionType.PHYSICAL_SHIPPED
      : InteractionType.PHYSICAL_DISTRIBUTED,
    title: `Physical Item ${shipped ? "Shipped" : "Distributed"}: ${params.itemName}`,
    purpose: "other",
    // There is no 'shipped'/'other' method (interactions_method_check). A shipped item is recorded
    // against the event ('event'); the type and metadata.distribution_method say how it went out.
    method: shipped ? "event" : "in_person",
    description:
      params.notes ||
      `${params.itemName} ${shipped ? "shipped" : "handed out"}${params.trackingNumber ? ` (Tracking: ${params.trackingNumber})` : ""}`,
    ...localDateAndTime(now),
    reward_type: "physical_good",
    direction: InteractionDirection.OUTBOUND,
    metadata: {
      distribution_method: params.distributionMethod,
      ...(params.trackingNumber
        ? { tracking_number: params.trackingNumber }
        : {}),
    },
  };
};

/** Log voucher issuance interaction */
export const logVoucherIssued = async (
  params: Parameters<typeof buildVoucherIssuedInteraction>[0],
): Promise<Interaction> =>
  insertRewardInteraction(buildVoucherIssuedInteraction(params));

/** Log offer link shared interaction */
export const logOfferLinkShared = async (
  params: Parameters<typeof buildOfferLinkSharedInteraction>[0],
): Promise<Interaction> =>
  insertRewardInteraction(buildOfferLinkSharedInteraction(params));

/** Log physical item distribution interaction */
export const logPhysicalDistributed = async (
  params: Parameters<typeof buildPhysicalDistributedInteraction>[0],
): Promise<Interaction> =>
  insertRewardInteraction(buildPhysicalDistributedInteraction(params));

/**
 * Run a reward-activity write without letting it block the reward. The reward has already been
 * issued when this runs; on failure the operator gets a non-blocking warning (it used to be
 * console.error only, which is how every reward log was lost unnoticed). Returns true when logged.
 */
export const recordRewardActivity = async (
  write: () => Promise<unknown>,
  warn: (message: string) => void,
): Promise<boolean> => {
  try {
    await write();
    return true;
  } catch (err) {
    console.error("Failed to log reward interaction:", err);
    const detail =
      err && typeof err === "object" && "message" in err
        ? String((err as { message: unknown }).message)
        : "";
    warn(
      `The reward was issued, but it could not be added to the contact's activity history${detail ? `: ${detail}` : "."}`,
    );
    return false;
  }
};

/**
 * Get reward interactions for a contact
 */
export const getContactRewardInteractions = async (
  contactId: UUID,
  limit: number = 20,
): Promise<Interaction[]> => {
  const { data, error } = await supabase
    .from("interactions")
    .select("*")
    .eq("contact_id", contactId)
    .in("type", [
      "voucher_issued",
      "offer_link_sent",
      "offer_link_copied",
      "physical_distributed",
      "physical_shipped",
      "reward_redeemed",
    ])
    .order("interaction_date", { ascending: false })
    .limit(limit);

  if (error) {
    throw error;
  }
  return data as Interaction[];
};

/**
 * Get reward interactions for an event
 */
export const getEventRewardInteractions = async (
  eventId: UUID,
  limit: number = 50,
): Promise<Interaction[]> => {
  const { data, error } = await supabase
    .from("interactions")
    .select("*, contact:contacts(first_name, last_name, email)")
    .eq("event_id", eventId)
    .in("type", [
      "voucher_issued",
      "offer_link_sent",
      "offer_link_copied",
      "physical_distributed",
      "physical_shipped",
      "reward_redeemed",
    ])
    .order("interaction_date", { ascending: false })
    .limit(limit);

  if (error) {
    throw error;
  }
  return data as Interaction[];
};
